/**
 * ImageUpload - Reusable component for uploading images with preview
 * Supports both URL input and file upload
 */
import { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, X, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { API, getAuthHeader } from './utils';

let uploadIdCounter = 0;

export const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  placeholder = "Enter URL or upload file",
  previewSize = "md", // sm, md, lg
  accept = "image/*",
  className = "",
  id = null // Optional unique ID
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(value ? 'url' : 'upload'); // 'url' or 'upload'
  const fileInputRef = useRef(null);
  // Generate a unique ID for this instance
  const [uniqueId] = useState(() => id || `img-upload-${++uploadIdCounter}-${Math.random().toString(36).substr(2, 9)}`);

  const sizeClasses = {
    sm: "w-12 h-12",
    md: "w-20 h-20",
    lg: "w-48 h-auto max-h-32"
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large. Maximum 10MB allowed.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/uploads/thumbnail`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.success) {
        onChange(res.data.url);
      } else {
        setError("Upload failed. Please try again.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlChange = (e) => {
    setError("");
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange("");
    setError("");
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400">
          {label}
        </label>
      )}

      {/* Mode Toggle */}
      <div className="flex gap-1 mb-2">
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`flex items-center gap-1 px-3 py-1 text-xs rounded transition-colors ${
            mode === 'url' 
              ? 'bg-orange-500 text-black' 
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          <LinkIcon className="w-3 h-3" />
          URL
        </button>
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`flex items-center gap-1 px-3 py-1 text-xs rounded transition-colors ${
            mode === 'upload' 
              ? 'bg-orange-500 text-black' 
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          <Upload className="w-3 h-3" />
          Upload
        </button>
      </div>

      {/* URL Input */}
      {mode === 'url' && (
        <div className="relative">
          <input
            type="text"
            value={value || ''}
            onChange={handleUrlChange}
            placeholder={placeholder}
            className="w-full bg-zinc-900 border border-white/10 text-white px-4 py-2 text-sm pr-8"
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* File Upload */}
      {mode === 'upload' && (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
            id={uniqueId}
          />
          <label
            htmlFor={uniqueId}
            className={`flex items-center justify-center gap-2 w-full bg-zinc-900 border border-dashed border-white/20 text-zinc-400 px-4 py-3 text-sm cursor-pointer hover:border-orange-500/50 hover:text-white transition-colors ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Click to upload image
              </>
            )}
          </label>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-red-500 text-xs">{error}</p>
      )}

      {/* Preview */}
      {value && (
        <div className="flex items-center gap-3 p-3 bg-zinc-800/50 border border-white/5 rounded">
          <div className={`${sizeClasses[previewSize]} flex-shrink-0 bg-white/5 rounded overflow-hidden relative`}>
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-contain"
              referrerPolicy="no-referrer"
              style={{ display: 'block' }}
              onError={(e) => {
                console.error('Image failed to load:', value);
                e.target.style.opacity = '0';
                // Show fallback icon
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div 
              className="w-full h-full items-center justify-center text-zinc-600 absolute inset-0 bg-zinc-800"
              style={{ display: 'none' }}
            >
              <ImageIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-zinc-400 truncate">{value}</p>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-red-500 hover:text-red-400 mt-1"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
